import {Injectable} from '@angular/core';
import * as _Highcharts from 'highcharts';
import {TranslateService} from '@ngx-translate/core';
import {ChartsHelperService} from '@services/charts/helper/charts-helper.service';
import {ChartAxisSettingsGeneratorService} from '@services/charts/line/axis/chart-axis-settings-generator.service';
import {
  ChartTooltipSettingsGeneratorService
} from '@services/charts/line/tooltip/chart-tooltip-settings-generator.service';
import {
  ChartLegendSettingsGeneratorService
} from '@services/charts/line/legend/chart-legend-settings-generator.service';
import {
  ChartSeriesSettingsGeneratorService
} from '@services/charts/line/series/chart-series-settings-generator.service';
import {
  ChartExportSettingsGeneratorService
} from '@services/charts/line/export/chart-export-settings-generator.service';
import {LineChartSettingsParameters} from '@models/charts/line-chart-settings-parameters';
import {BehaviorSubject} from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class ChartSettingsGeneratorService {
  private zoomChangeBehaviourSubject = new BehaviorSubject(false);
  public zoomChangeNotification = this.zoomChangeBehaviourSubject.asObservable();

  constructor(private translationService: TranslateService,
              private chartsHelperService: ChartsHelperService,
              private chartAxisGeneratorService: ChartAxisSettingsGeneratorService,
              private chartTooltipGeneratorService: ChartTooltipSettingsGeneratorService,
              private chartLegendGeneratorService: ChartLegendSettingsGeneratorService,
              private chartSeriesGeneratorService: ChartSeriesSettingsGeneratorService,
              private chartExportService: ChartExportSettingsGeneratorService) {
  }

  public generateChartOptions(chartParameters: LineChartSettingsParameters): _Highcharts.Options {
    const self = this;
    return {
      chart: {
        animation: false,
        shadow: false,
        zooming: {type: 'x'},
        alignTicks: true,
        marginTop: 20,
        panning: {
          enabled: true,
          type: 'x'
        },
        style: {
          fontFamily: '"roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif'
        },
        panKey: 'shift',
        events: {
          render: function (event) {
            this.reflow();
            self.renderChart(event, chartParameters.isLineChart);
          },
          selection: function (event) {

            if (chartParameters.isInEditMode) {
              return false;
            }

            if (event.xAxis) {
              console.log('zoomed in');
              self.zoomChangeBehaviourSubject.next(true);
            } else {
              console.log('zoomed out');
              self.zoomChangeBehaviourSubject.next(false);
            }
            return true;

          }
        }
      },
      title: {
        text: undefined
      },
      yAxis: this.chartAxisGeneratorService.createAllYAxis(chartParameters.isLineChart, chartParameters.machineSignals, chartParameters.chartSettings),
      xAxis: this.chartAxisGeneratorService.createXAxis(chartParameters.isLineChart, chartParameters.chartSettings, chartParameters.data, chartParameters.stringData, chartParameters.portalLanguage),
      credits: {
        enabled: false,
        text: String(chartParameters.chartId)//!!!DO NOT REMOVE. needed to identify the chart by id
      },
      tooltip: this.chartTooltipGeneratorService.generateTooltipSettings(chartParameters.chartSettings, chartParameters.machineSignals, chartParameters.stringData, chartParameters.isLineChart, chartParameters.isInEditMode, chartParameters.portalLanguage, chartParameters.data),
      legend: this.chartLegendGeneratorService.generateLegendSettings(chartParameters.isLineChart, chartParameters.machineId, chartParameters.isInEditMode, chartParameters.chartId),
      plotOptions: {
        series: {
          animation: false,
          showCheckbox: !chartParameters.isLineChart,
          connectNulls: true,
          shadow: false,
          marker: {
            enabled: false
          },
          events: {
            checkboxClick: (event) => {
              this.chartLegendGeneratorService.onLegendSignalCheckBoxClick(chartParameters.machineId, event);
            }
          }
        }
      },
      boost: {
        enabled: true,
        useGPUTranslations: false,
      },
      series: this.chartSeriesGeneratorService.createSeries(chartParameters.isLineChart, chartParameters.machineId, chartParameters.chartSettings.signals, chartParameters.chartSettings.axis, chartParameters.data, chartParameters.machineSignals),
      exporting: this.chartExportService.generateExportingSettings(chartParameters.isLineChart, chartParameters.chartSettings)


    };
  }

  private renderChart(event, isLineChart: boolean) {
    if (!event || !event.target) {
      return;
    }
    const chart = event.target;

    chart.width = null;
    chart.height = '100%';
    chart.chartIsReady = true;
    if (!isLineChart) {
      for (const item of chart.legend.allItems) {
        item.checkbox.title = this.translationService.instant('Show min/max');
      }
    }

    this.chartAxisGeneratorService.togglePlotBands(chart.xAxis);
  }


}
